// Shared chart aggregation helpers used by both the per-data-type DataChart
// and the combined multi-series overlay chart. Centralized so totals stay
// identical between the single-type view and the overlay view.

import { startOfWeek, startOfMonth, format, addDays, addWeeks, addMonths } from 'date-fns';
import { parseCalendarDate } from './dateTime';

// For rollup data types, each cached record is a per-facility-per-day summary,
// not an individual event. Sum the counter fields below to get true activity
// totals so the chart matches the Home page KPIs. Referrals / VitalRounds
// remain row-counted because each cached row is one event.
export const ROLLUP_ACTIVITY_FIELDS = {
  studies: ['study_sent', 'study_received'],
  logins: ['web_login', 'mobile', 'desktop_login', 'goweb_login', 'telemedicine_login', 'common_login'],
  messages: ['referral_messages_sent', 'vmail_messages_Sent', 'vchat_messages_Sent'],
};

// Studies cache one row per facility-per-day with both directions on it; the
// chart can sum just one direction or both.
export const STUDY_METRIC_FIELDS = {
  sent:     ['study_sent'],
  received: ['study_received'],
  total:    ['study_sent', 'study_received'],
};

// The individual VitalEngine message channels that roll up into "Messages".
// Shared so the per-type DataChart and the combined/overlay view offer the
// exact same set. NOTE: the combined total uses ROLLUP_ACTIVITY_FIELDS.messages
// (referral + vmail + vchat sent) to match the Home page KPIs; the per-type
// picker below can additionally isolate any single channel.
export const MESSAGE_TYPE_FIELDS = [
  { field: 'referral_messages_sent', label: 'Referral' },
  { field: 'vmail_messages_Sent', label: 'VMail' },
  { field: 'vchat_messages_Sent', label: 'VChat Sent' },
  { field: 'vchat_messages_received', label: 'VChat Received' },
  { field: 'vround_messages_sent', label: 'VRounds' },
  { field: 'vtransfer_messages_sent', label: 'VTransfer' },
  { field: 'Other_messages_sent', label: 'Other' },
];

export const MESSAGE_FIELD_ALIASES = {
  referral_messages_sent: ['referral_messages_sent'],
  vmail_messages_Sent: ['vmail_messages_Sent', 'vmail_messages_sent'],
  vchat_messages_Sent: ['vchat_messages_Sent', 'vchat_messages_sent'],
  vchat_messages_received: ['vchat_messages_received', 'vchat_messages_Received'],
  vround_messages_sent: ['vround_messages_sent', 'vround_messages_Sent'],
  vtransfer_messages_sent: ['vtransfer_messages_sent', 'vtransfer_messages_Sent'],
  Other_messages_sent: ['Other_messages_sent', 'other_messages_sent'],
};

export function getMessageFieldValue(recordData, field) {
  if (!recordData || !field) return 0;
  const keys = MESSAGE_FIELD_ALIASES[field] || [field];
  for (const k of keys) {
    const v = Number(recordData[k]);
    if (!Number.isNaN(v)) return v;
  }
  return 0;
}

export function getActivityCount(record, dataType, fieldsOverride) {
  const fields = fieldsOverride || ROLLUP_ACTIVITY_FIELDS[dataType];
  if (!fields) return 1;
  const d = record.data || record.Data || {};
  if (dataType === 'messages') {
    let total = 0;
    for (const f of fields) total += getMessageFieldValue(d, f);
    return total;
  }
  let total = 0;
  for (const f of fields) {
    const v = Number(d[f]);
    if (!Number.isNaN(v)) total += v;
  }
  return total;
}

export function getBinKey(date, binning) {
  switch (binning) {
    case 'week':
      return format(startOfWeek(date, { weekStartsOn: 0 }), 'yyyy-MM-dd');
    case 'month':
      return format(startOfMonth(date), 'yyyy-MM-dd');
    case 'day':
    default:
      return format(date, 'yyyy-MM-dd');
  }
}

// Returns every bin key between windowStart and windowEnd (inclusive) at the
// requested granularity. Used by the chart aggregations to zero-fill empty
// buckets.
export function enumerateBins(windowStart, windowEnd, binning) {
  if (!windowStart || !windowEnd) return null;
  const start = parseCalendarDate(windowStart);
  const end = parseCalendarDate(windowEnd);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) return null;

  const bins = [];
  if (binning === 'week') {
    let cursor = startOfWeek(start, { weekStartsOn: 0 });
    const last = startOfWeek(end, { weekStartsOn: 0 });
    while (cursor <= last) {
      bins.push(format(cursor, 'yyyy-MM-dd'));
      cursor = addWeeks(cursor, 1);
    }
  } else if (binning === 'month') {
    let cursor = startOfMonth(start);
    const last = startOfMonth(end);
    while (cursor <= last) {
      bins.push(format(cursor, 'yyyy-MM-dd'));
      cursor = addMonths(cursor, 1);
    }
  } else {
    let cursor = new Date(start);
    cursor.setHours(0, 0, 0, 0);
    const last = new Date(end);
    last.setHours(0, 0, 0, 0);
    while (cursor <= last) {
      bins.push(format(cursor, 'yyyy-MM-dd'));
      cursor = addDays(cursor, 1);
    }
  }
  return bins;
}

export function getDataTypeLabel(dataType, studyMetric) {
  if (dataType === 'studies') {
    if (studyMetric === 'sent')     return 'Studies Sent';
    if (studyMetric === 'received') return 'Studies Received';
    return 'Studies (Sent + Received)';
  }
  const labels = {
    referrals: 'Referrals',
    vitalrounds: 'VitalRounds',
    messages: 'Messages',
    logins: 'Logins',
    studies: 'Studies',
    'group-referrals': 'Group Referrals',
    faxes: 'Faxes',
    'fax-recipients': 'Fax Reads',
    'study-details': 'DICOM Studies',
  };
  return labels[dataType] || 'Records';
}
